/**
 * Explore category card design assets, keyed by lowercased API `category_name`.
 * Colors and images are design-provided pairs; files live in
 * `apps/vizit/public/images/explore-categories/`.
 */
const EXPLORE_CATEGORY_COLORS: Readonly<Record<string, string>> = Object.freeze({
  automotive: '#E2CBFF',
  baby: '#D1D0FF',
  beauty: '#CCD7FF',
  electronics: '#CCECFF',
  'grocery & gourmet food': '#CCF8FF',
  'health & household': '#D1FDF5',
  'office products': '#E0FFD7',
  'patio, lawn & garden': '#EFFCC5',
  'pet supplies': '#F6F9BF',
  'sports & outdoors': '#FEF8B9',
  'tools & home improvement': '#FFECBD',
  'toys & games': '#FFD3C8',
});

const DEFAULT_EXPLORE_CATEGORY_COLOR = '#F5F5F5';

const EXPLORE_CATEGORY_IMAGES: Readonly<Record<string, string>> = Object.freeze({
  automotive: '/images/explore-categories/automotive.png',
  baby: '/images/explore-categories/baby.png',
  beauty: '/images/explore-categories/beauty.png',
  electronics: '/images/explore-categories/electronics.png',
  'grocery & gourmet food': '/images/explore-categories/grocery-gourmet-food.png',
  'health & household': '/images/explore-categories/health-household.png',
  'office products': '/images/explore-categories/office-products.png',
  'patio, lawn & garden': '/images/explore-categories/patio-lawn-garden.png',
  'pet supplies': '/images/explore-categories/pet-supplies.png',
  'sports & outdoors': '/images/explore-categories/sports-outdoors.png',
  'tools & home improvement': '/images/explore-categories/tools-home-improvement.png',
  'toys & games': '/images/explore-categories/toys-games.png',
});

/**
 * Canonical lookup key for a category name: lowercased, with standalone "and"
 * normalized to "&" so either spelling resolves to the same design assets.
 */
function normalizeExploreCategoryName(categoryName: string): string {
  return categoryName
    .trim()
    .toLowerCase()
    .replace(/\band\b/g, '&');
}

function getExploreCategoryColor(categoryName: string): string {
  return EXPLORE_CATEGORY_COLORS[normalizeExploreCategoryName(categoryName)] ?? DEFAULT_EXPLORE_CATEGORY_COLOR;
}

function getExploreCategoryImageUrl(categoryName: string | null | undefined): string | undefined {
  if (!categoryName) return undefined;
  return EXPLORE_CATEGORY_IMAGES[normalizeExploreCategoryName(categoryName)];
}

function getExploreCategoryDisplayName(categoryName: string): string {
  return categoryName.replace(/\band\b/gi, '&');
}

export {
  normalizeExploreCategoryName,
  EXPLORE_CATEGORY_COLORS,
  EXPLORE_CATEGORY_IMAGES,
  DEFAULT_EXPLORE_CATEGORY_COLOR,
  getExploreCategoryColor,
  getExploreCategoryImageUrl,
  getExploreCategoryDisplayName,
};
